import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { ImageLoader } from './image-loader';

const defaultLoader = new ImageLoader();
export const ImageLoaderContext = createContext<ImageLoader>(defaultLoader);

export interface LazyImageProps {
  src: string | null;
  /** Shown as initials while loading and when the image is missing or broken. */
  label: string;
  className?: string;
}

/** How far outside the screen a picture starts loading. */
const NEAR_SCREEN = '200px';

/**
 * Image with a text fallback; loading goes through the shared `ImageLoader`.
 * A picture starts loading only when it is on or near the screen, so a page of
 * many rows (Movies, Series) loads what the viewer sees first, and a TV doesn't
 * decode hundreds of posters at once.
 */
export function LazyImage({ src, label, className }: LazyImageProps) {
  const loader = useContext(ImageLoaderContext);
  const [state, setState] = useState<'loading' | 'loaded' | 'failed'>(() => initialState(loader, src));
  const placeholder = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!src) {
      setState('failed');
      return undefined;
    }
    const known = loader.status(src);
    if (known !== 'unknown') {
      setState(known === 'loaded' ? 'loaded' : 'failed');
      return undefined;
    }
    setState('loading');
    const start = () => loader.load(src, (ok) => setState(ok ? 'loaded' : 'failed'));
    const element = placeholder.current;
    if (!element || typeof IntersectionObserver !== 'function') return start();
    let cancel: (() => void) | null = null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (cancel || !entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        cancel = start();
      },
      { rootMargin: NEAR_SCREEN },
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
      cancel?.();
    };
  }, [loader, src]);

  if (state === 'loaded' && src) {
    return <img className={className} src={src} alt="" draggable={false} />;
  }
  return (
    <span ref={placeholder} className={`${className ?? ''} image-fallback`} data-state={state} aria-hidden="true">
      {initials(label)}
    </span>
  );
}

function initialState(loader: ImageLoader, src: string | null): 'loading' | 'loaded' | 'failed' {
  if (!src) return 'failed';
  const known = loader.status(src);
  return known === 'unknown' ? 'loading' : known === 'loaded' ? 'loaded' : 'failed';
}

export function initials(label: string): string {
  const words = label
    .replace(/^[A-Z]{2,3}\s*[|:-]\s*/, '')
    .split(/[^\p{L}\p{N}]+/u)
    .filter((word) => word.length > 0);
  // Prefer words starting with a letter, so "Office (1973)" is "O", not "O1".
  const lettered = words.filter((word) => /^\p{L}/u.test(word));
  return (lettered.length > 0 ? lettered : words)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
}
